import React from 'react';
import { 
  TrendingUp, 
  PieChart, 
  Target, 
  Flame, 
  Trophy, 
  Users,
  Sparkles,
  Bell
} from 'lucide-react';

const FeaturesShowcaseSection = () => {
  const features = [
    {
      icon: TrendingUp,
      title: 'Credit Score Tracker',
      description: 'Track your own credit score over time, privately and securely — never shared with credit bureaus',
      color: '#D4AF37',
    },
    {
      icon: PieChart,
      title: 'Net Worth Tracking',
      description: 'Your whole financial picture in one number',
      color: '#95B8A2',
    },
    {
      icon: Target,
      title: 'Debt Payoff Tracker',
      description: 'Crush debt with Avalanche or Snowball strategies',
      color: '#E74C3C',
    },
    {
      icon: Flame,
      title: 'Daily Streaks & XP',
      description: 'Build money habits that actually stick',
      color: '#F39C12',
    },
    {
      icon: Trophy,
      title: 'Spending Challenges',
      description: 'Saving, gamified',
      color: '#2ECC71',
    },
    {
      icon: Users,
      title: 'Household / Couples Mode',
      description: 'Better together — shared goals, budgets, and a monthly Money Date',
      color: '#95B8A2',
    },
    {
      icon: Sparkles,
      title: 'Make It Yours',
      description: 'Upload a photo and let Findell generate a personalized AI avatar — or keep your original photo. Your choice, any time.',
      color: '#D4AF37',
    },
    {
      icon: Bell,
      title: 'Subscription Watchdog',
      description: 'Findell automatically detects recurring subscriptions in your linked accounts, flags ones you might not need, and drafts the cancellation email for you.',
      color: '#F39C12',
    },
  ];

  return (
    <section
      id="feature-showcase"
      data-testid="features-showcase-section"
      className="bg-[#0B1F17] section-padding relative overflow-hidden"
    >
      {/* Ambient glow effects */}
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[120px] animate-ambient-glow" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#95B8A2]/5 rounded-full blur-[100px] animate-ambient-glow" style={{ animationDelay: '2s' }} />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <p
            data-testid="features-showcase-label"
            className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#D4AF37] mb-4"
          >
            More Than a Budget App
          </p>
          <h2
            data-testid="features-showcase-headline"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FAFAF7] leading-tight mb-4"
          >
            Everything You Need to
            <br />
            Master Your Money.
          </h2>
          <p className="font-body text-lg text-[#FAFAF7]/60 max-w-2xl mx-auto">
            Findell combines powerful tools with AI coaching to help you understand, grow, and protect your finances.
          </p>
        </div>

        {/* Feature Cards Grid - Heritage Glassmorphism */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              data-testid={`feature-card-${index}`}
              className="group glass-heritage rounded-2xl p-6 animate-fade-in-up transition-all duration-500 hover:-translate-y-2"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Icon with glow */}
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                style={{ 
                  backgroundColor: `${feature.color}20`,
                  boxShadow: `0 0 20px ${feature.color}20`
                }}
              >
                <feature.icon 
                  className="w-7 h-7 transition-all duration-300" 
                  style={{ color: feature.color }}
                />
              </div>
              
              {/* Title */}
              <h3 className="font-heading text-xl font-bold text-[#FAFAF7] mb-2">
                {feature.title}
              </h3>
              
              {/* Description */}
              <p className="font-body text-sm text-[#FAFAF7]/70 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <a
            href="#pricing"
            data-testid="features-showcase-cta"
            className="inline-flex items-center gap-2 font-body font-semibold text-[#D4AF37] hover:text-[#e6c455] transition-colors"
          >
            See What's Included in Each Plan →
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturesShowcaseSection;
